import type {
  BeginCheckoutParams,
  PurchaseParams,
  SubscriptionAnalyticsPayload,
} from "@/app/utils/subscriptionAnalytics";

type PricingLinkClick = {
  event: "pricing_click";
  params: {
    from: string;
  };
};

type MiniAgentTryEvent = {
  event: "mini_agent_try";
  params: {
    agentId: string;
    agentName: string;
  };
};

type PricingTierClick = {
  event: "pricing_tier_click";
  params: {
    tier: string;
  };
};

type FolioAiRatingEvent = {
  event: "folio_ai_rating";
  params: {
    sentiment: "positive" | "negative";
    source: string;
    appVersion: string;
    locale: string;
  };
};

type SubscriptionEvent = {
  event: "subscription";
  params: SubscriptionAnalyticsPayload;
};

// GA4 standard ecommerce events (distinct from the custom `subscription` event above): begin_checkout
// fires before the redirect to Stripe, purchase on subscription success.
type BeginCheckoutEvent = {
  event: "begin_checkout";
  params: BeginCheckoutParams;
};

type PurchaseEvent = {
  event: "purchase";
  params: PurchaseParams;
};

type DeeplinkOpenedEvent = {
  event: "deeplink_opened";
  params: { destination: string; utm_campaign?: string };
};

// Fired by `/webinar` on its way to the registration page, which is not ours.
type WebinarClickEvent = {
  event: "webinar_click";
  params: { utm_source?: string; utm_campaign?: string };
};

type AnalyticsEvent =
  | DeeplinkOpenedEvent
  | WebinarClickEvent
  | PricingLinkClick
  | MiniAgentTryEvent
  | PricingTierClick
  | FolioAiRatingEvent
  | SubscriptionEvent
  | BeginCheckoutEvent
  | PurchaseEvent;

// gtag transport controls (not analytics dimensions): event_callback runs once the hit is sent or
// event_timeout elapses. Kept separate from the typed payload so an event's params stay pure data.
type GtagTransportOptions = {
  event_callback?: () => void;
  event_timeout?: number;
};

// GA4's gtag takes a command as the first argument, so an event is sent as
// gtag("event", "<name>", params) — not gtag("<name>", params), which GA4 ignores.
export function trackEvent(
  event: AnalyticsEvent,
  options?: GtagTransportOptions,
) {
  (window as any)?.gtag?.("event", event.event, {
    ...event.params,
    ...options,
  });
}
